import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import ContentHeader from '../Layout/ContentHeader'
import { toastSweetAlert } from '../Helpers/ToastSweetAlert'
import Box from '../Global/Box'
import InputController from '../Global/InputController'
import { useLoading } from '../../LoadingOverlay/LoadingOverlayContext'
import {
  arrayCategoryBlog,
  arrayLayoutBlog,
  fnArraySelectStatusBlog,
} from '../../const/constBlogPost'
import {
  CREATE_BLOG_POST,
  GET_ALL_BLOG_POSTS,
  GET_ONE_BLOG_POST,
  UPDATE_BLOG_POST,
  UPDATE_BLOG_POST_ONLY_FILE,
  UPDATE_BLOG_POST_ONLY_FILE_DELETE,
} from '../../graphql/BlogPosts/blogPosts'

const extensions_allowed_image = ['.png', '.jpg', '.jpeg']
const sizeMaxFile = 8 // EXPRESADO EN MB.
const optionsStatusBlog = fnArraySelectStatusBlog()
const refetchBlogs = {
  query: GET_ALL_BLOG_POSTS,
  variables: {
    limit: 10,
    offset: 0,
    searchQuery: null,
  },
  fetchPolicy: 'no-cache',
}

export const BlogPostsNew = () => {
  const { id: _id } = useParams()
  const history = useHistory()
  const { showLoading, hideLoading } = useLoading()

  //STATES:
  const [data, setData] = useState(null)
  const [file, setFile] = useState(null)

  //MUTATIONS:
  const [createPost] = useMutation(CREATE_BLOG_POST, {
    refetchQueries: [{ ...refetchBlogs }],
  })

  const [updatePostOnlyFile] = useMutation(UPDATE_BLOG_POST_ONLY_FILE, {
    refetchQueries: [
      { ...refetchBlogs },
      { query: GET_ONE_BLOG_POST, variables: { id: parseInt(_id) } },
    ],
  })

  const [updatePostOnlyFileDelete] = useMutation(
    UPDATE_BLOG_POST_ONLY_FILE_DELETE,
    {
      refetchQueries: [
        { ...refetchBlogs },
        { query: GET_ONE_BLOG_POST, variables: { id: parseInt(_id) } },
      ],
    }
  )

  const [updatePost] = useMutation(UPDATE_BLOG_POST, {
    refetchQueries: [
      { ...refetchBlogs },
      { query: GET_ONE_BLOG_POST, variables: { id: parseInt(_id) } },
    ],
  })

  //QUERIES:
  const {
    data: dataResp,
    loading,
    error,
  } = useQuery(GET_ONE_BLOG_POST, {
    variables: {
      id: parseInt(_id),
    },
  })

  //FORM:
  const validationSchema = Yup.object().shape({
    layout: Yup.string().required('Campo requerido'),
    category: Yup.string().required('Campo requerido'),
    title: Yup.string().required('Campo requerido'),
    content: Yup.string().required('Campo requerido'),
    summary_content: Yup.string().required('Campo requerido'),
    status_id: Yup.string().required('Campo requerido'),
  })

  const {
    methods,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  //EFFECTS:
  useEffect(() => {
    if (!loading) {
      if (_id) {
        if (error) {
          return toastSweetAlert({
            mode: 'error',
            message: error.message,
          })
        }

        if (dataResp !== undefined && dataResp !== null) {
          const data = dataResp.getOneBlogPost

          setData(data)

          const dataFormated = {
            layout: data.layout,
            category: data.category,
            title: data.title,
            content: data.content,
            summary_content: data.summary_content,
            external_link: data.external_link,
            external_link_label: data.external_link_label,
            status_id: data.status_id,
          }

          reset(dataFormated)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id, dataResp, error, loading])

  const handleFile = async (e) => {
    if (!e.target.files[0]) return

    const fileName = e.target.files[0].name.toLowerCase()
    const lastDot = fileName.lastIndexOf('.')
    const extension = fileName.slice(lastDot, fileName.length)

    if (extensions_allowed_image.indexOf(extension) === -1) {
      setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: 'Solo se admite JPG, JPGE y PNG',
      })
    }

    if (e.target.files[0].size / 1048576 > sizeMaxFile) {
      setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: `El archivo no puede superar los ${sizeMaxFile}MB`,
      })
    }

    setFile(e.target.files[0])
  }

  const handleSubmitUpdateOnlyFile = async () => {
    if (!file) {
      return toastSweetAlert({
        mode: 'errorToast',
        message: 'Se requiere elegir imagen',
      })
    }

    const input = { file, image_file_type: 'img' }
    try {
      await updatePostOnlyFile({
        variables: { blogPostId: parseInt(_id), input },
      })
      return toastSweetAlert({ mode: 'ok', message: 'Se actualizo la imagen' })
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }

  const handleSubmitUpdateOnlyFileDelete = async () => {
    try {
      await updatePostOnlyFileDelete({
        variables: { blogPostId: parseInt(_id) },
      })
      return toastSweetAlert({ mode: 'ok', message: 'Se elimino la imagen' })
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }

  const onSubmit = async (dataForm) => {
    const input = {
      layout: dataForm.layout,
      category: dataForm.category,
      title: dataForm.title,
      content: dataForm.content,
      summary_content: dataForm.summary_content,
      external_link: dataForm.external_link,
      external_link_label: dataForm.external_link_label,
      status_id: parseInt(dataForm.status_id),
    }

    showLoading()

    try {
      let message = ''

      if (_id) {
        await updatePost({ variables: { blogPostId: parseInt(_id), input } })
        message = 'Se actualizo correctamente'
      } else {
        if (file) {
          input.file = file
          input.image_file_type = 'img'
        }

        await createPost({ variables: { input } })
        message = 'Se creo con exito'
      }

      return toastSweetAlert(
        { mode: 'ok', message },
        history.push(`/BlogPosts`)
      )
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    } finally {
      hideLoading()
    }
  }

  return (
    <>
      <ContentHeader
        title="Blog"
        breadcrumb=""
        windowTitle={`${_id ? 'Editar' : 'Agregar'} post `}
      />
      <FormProvider {...methods}>
        <form className="p-3" onSubmit={handleSubmit(onSubmit)}>
          <Box
            title={`${_id ? 'Editar' : 'Agregar nuevo'} post`}
            btnRedPath="/BlogPosts"
            btnRedTxt="Cancelar"
            errors={errors}
            btnSubmit={true}
            content={
              <>
                <div className="row">
                  <div className="mb-3 col-12 col-sm-4 col-md-3">
                    <InputController
                      label="Estatus"
                      name="status_id"
                      control={control}
                      inputType="choosen"
                      options={optionsStatusBlog}
                      placeholderTxt="Elige uno"
                      isClearable={true}
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-4 col-md-3">
                    <InputController
                      label="Plantilla"
                      name="layout"
                      control={control}
                      inputType="choosen"
                      options={arrayLayoutBlog}
                      placeholderTxt="Elige uno"
                      isClearable={true}
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-4 col-md-3">
                    <InputController
                      label="Categoria"
                      name="category"
                      control={control}
                      inputType="choosen"
                      options={arrayCategoryBlog}
                      placeholderTxt="Elige una"
                      isClearable={true}
                    />
                  </div>
                  <div className="mb-3 col-12">
                    <InputController
                      label="Titulo"
                      name="title"
                      type="text"
                      placeholderTxt="Escribe titulo"
                      control={control}
                    />
                  </div>
                  <div className="mb-3 col-12">
                    <InputController
                      label="Contenido completo"
                      name="content"
                      inputType="textarea"
                      placeholderTxt="Escribe contenido completo"
                      control={control}
                      rows={15}
                      textAreaNotResize={true}
                    />
                  </div>
                  <div className="mb-3 col-12">
                    <InputController
                      label="Contenido resumido (breve)"
                      name="summary_content"
                      inputType="textarea"
                      placeholderTxt="Escribe contenido resumido (breve)"
                      control={control}
                      rows={2}
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-4">
                    <InputController
                      label="Enlace externo (label)"
                      name="external_link_label"
                      type="text"
                      placeholderTxt="Escribe label del link externo"
                      control={control}
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-8">
                    <InputController
                      label="Enlace externo (url)"
                      name="external_link"
                      type="text"
                      placeholderTxt="Escribe url link externo"
                      control={control}
                    />
                  </div>
                </div>
                {!_id && (
                  <div className="row">
                    <div className="mb-3 col-12 col-sm-6">
                      <InputController
                        label="Imagen"
                        inputType="file"
                        formatAccept="image/*"
                        validateFormat="imageavatar"
                        validateFormatMsg="Solo se admite JPG, JPGE y PNG"
                        name="file"
                        control={control}
                        changeAction={(e) => handleFile(e)}
                      />
                    </div>
                  </div>
                )}
              </>
            }
          />
        </form>
      </FormProvider>

      {_id && (
        <div className="mt-2 p-3">
          <Box
            title="Subir o actualizar imagen"
            btnRedPath="/BlogPosts"
            btnRedTxt="Cancelar"
            btnSubmitNoEnter={() => handleSubmitUpdateOnlyFile()}
            btnSubmitNoEnterTxtSave={`${
              data?.image_file_url && data?.image_file_id
                ? 'Actualizar imagen'
                : 'Subir imagen'
            }`}
            btnSubmitNoEnterTxtUpdate={`${
              data?.image_file_url && data?.image_file_id
                ? 'Actualizar imagen'
                : 'Subir imagen'
            }`}
            content={
              <div className="row mb-5">
                <div className="mb-3 col-12 col-sm-6">
                  <InputController
                    label="Imagen"
                    inputType="file"
                    formatAccept="image/*"
                    validateFormat="imageavatar"
                    validateFormatMsg="Solo se admite JPG, JPGE y PNG"
                    name="file"
                    control={control}
                    changeAction={(e) => handleFile(e)}
                  />
                </div>
                {data?.image_file_url && data?.image_file_id && (
                  <div className="col-12 col-sm-6">
                    <label>Imagen actual</label>
                    <div>
                      <img
                        src={data.image_file_url}
                        width={'100%'}
                        alt="Imagen"
                      />
                    </div>
                    <button
                      className="btn btn-danger mt-3 float-right"
                      onClick={() => handleSubmitUpdateOnlyFileDelete()}
                    >
                      <i className="fas fa-trash-alt" /> Eliminar imagen
                    </button>
                  </div>
                )}
              </div>
            }
          />
        </div>
      )}
    </>
  )
}
export default BlogPostsNew
